import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { Alert, Input, Select, theme } from 'antd';
import { locationTextKind, validPin } from '../../models/customer/addressLocationModel';
import { useAddressLocation } from '../../viewmodels/customer/useAddressLocation';
import { useMaps } from '../../../../features/maps/viewmodels/MapsProvider';
import PlaceSearchInput from '../../../../features/maps/views/PlaceSearchInput';

const DARK_MAP = [{ elementType: 'geometry', stylers: [{ color: '#242b30' }] }, { elementType: 'labels.text.fill', stylers: [{ color: '#c5ced3' }] }, { elementType: 'labels.text.stroke', stylers: [{ color: '#242b30' }] }];
export function AddressLocationPicker({ form, onChange, isLoaded, isDark, locationMessages = [], showAddressText = true }) {
  const { token } = theme.useToken();
  const { unavailable } = useMaps();
  const location = useAddressLocation(onChange, form.address || '', showAddressText);
  const cancelReverseGeocode = location.cancelReverseGeocode;
  useEffect(() => { if (form.no_pin_reason) cancelReverseGeocode(); }, [form.no_pin_reason]); // eslint-disable-line react-hooks/exhaustive-deps
  const pinned = validPin(form.latitude, form.longitude);
  const center = pinned ? { lat: Number(form.latitude), lng: Number(form.longitude) } : { lat: 25.2, lng: 55.3 };
  const initialCenter = useRef(center), initialZoom = useRef(pinned ? 16 : 10), map = useRef(null);
  const navigate = () => {
    if (!map.current || !location.navigation) return;
    map.current.panTo({ lat: location.navigation.latitude, lng: location.navigation.longitude });
    map.current.setZoom(16);
  };
  useEffect(navigate, [location.navigation]); // eslint-disable-line react-hooks/exhaustive-deps
  // One field: a place name searches; a Maps link or coordinates set the pin
  // (at once when pasted, on Enter when typed), then the field shows the pin's address.
  const [searchText, setSearchText] = useState('');
  const [showPin, setShowPin] = useState(false);
  useEffect(() => { if (showPin && location.pinLabel) setSearchText(location.pinLabel); }, [showPin, location.pinLabel]);
  const pinFromText = text => {
    if (!locationTextKind(text)) return false;
    setShowPin(false);
    location.pastePin(text).then(ok => { if (ok) setShowPin(true); });
    return true;
  };
  const kind = locationTextKind(searchText);
  const hint = location.error ? null
    : kind === 'link' ? (location.resolving ? 'Reading the Maps link…' : 'Maps link detected')
    : kind === 'coordinates' ? 'Coordinates detected. Press Enter to set the pin.' : null;
  return <div style={{ display: 'grid', gap: 8, color: token.colorText }}>
    {locationMessages.length > 0 && <Select placeholder="Use a location from this chat" value={null} options={locationMessages.map((message, index) => ({ value: index, label: message.content.name || message.content.address || 'Shared location' }))} onChange={index => {
      const content = locationMessages[index].content;
      location.movePin(Number(content.latitude), Number(content.longitude), 'chat', content.address || content.name || '');
    }} />}
    <PlaceSearchInput
      value={searchText}
      onChange={text => { setSearchText(text); setShowPin(false); }}
      placeholder="Search a place, or paste a Maps link or coordinates"
      ariaLabel="Search a place or paste a Maps link"
      isLocationText={text => Boolean(locationTextKind(text))}
      onPasteText={pinFromText}
      onEnter={pinFromText}
      loading={location.resolving}
      hint={hint}
      onSelect={place => {
        location.cancelReverseGeocode();
        if (validPin(place?.latitude, place?.longitude)) location.selectPlace(place);
      }} />
    {isLoaded ? <>
      <GoogleMap mapContainerStyle={{ height: 260, width: '100%', borderRadius: 8 }} center={initialCenter.current} zoom={initialZoom.current} onLoad={value => { map.current = value; navigate(); }} onUnmount={() => { map.current = null; }} options={{ styles: isDark ? DARK_MAP : undefined, streetViewControl: false, mapTypeControl: false }} onClick={event => location.movePin(event.latLng.lat(), event.latLng.lng())}>
        {pinned && <MarkerF position={center} draggable onDragEnd={event => location.movePin(event.latLng.lat(), event.latLng.lng())} />}
      </GoogleMap>
    </> : <Alert type={unavailable ? 'warning' : 'info'} message={unavailable ? 'Map unavailable. Search and pasted coordinates still set the pin.' : 'Map loading. You can still search, or paste a link or coordinates above.'} />}
    <small style={{ color: pinned ? token.colorSuccess : token.colorTextSecondary }}>{pinned ? `✓ Pin set: ${location.pinLabel || `${form.latitude}, ${form.longitude}`}` : 'No pin yet: search, paste a link, or click the map'}</small>
    {location.error && <Alert type="warning" message={location.error} />}
    {showAddressText && <Input.TextArea placeholder="Address *" value={form.address || ''} autoSize={{ minRows: 2, maxRows: 5 }} onChange={event => { location.preserveTypedAddress(); onChange({ address: event.target.value }); }} />}
  </div>;
}
